import {useEffect, useState} from 'react';
import {Alert} from '@vector';
import {hotmathsContent} from './content/hotmaths';
import {fetchConfig} from './homeService';
import Layout from './components/Layout';
import Hero from './components/Hero';
import FeatureGrid from './components/FeatureGrid';
import TrialPanel from './components/TrialPanel';
import PricingPanel from './components/PricingPanel';
import QuotePanel from './components/QuotePanel';
import VideoSection from './components/VideoSection';
import SampleLessons from './components/SampleLessons';
import AboutSection from './components/AboutSection';
import BenefitsSection from './components/BenefitsSection';
import OtherProductsCard from './components/OtherProductsCard';
import styles from './HomeUpdated.module.css';

/**
 * Redesigned public HOTmaths homepage - implements the design handoff
 * (`design_handoff_homepage_redesign/`) as a composition of `components/*`, each with its own
 * CSS Module. A prototype fork: nothing here is shared with `Home.js`, which stays untouched.
 */
export default function HomeUpdated({content = hotmathsContent}) {
    const [config, setConfig] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetchConfig()
            .then((loaded) => !cancelled && setConfig(loaded))
            .catch(() => { /* defaults below are fine */ });
        return () => {
            cancelled = true;
        };
    }, []);

    // A link to a section (/#ch-home-about, and the old About/Benefits pages redirect to one) arrives before React
    // has rendered it, so the browser's own jump to the anchor finds nothing. Do it once the page is there.
    // Repeated once fonts and images have loaded, because they push the section further down the page.
    useEffect(() => {
        const id = window.location.hash.slice(1);
        if (!id) {
            return undefined;
        }
        const jump = () => document.getElementById(id)?.scrollIntoView();
        jump();
        if (document.readyState === 'complete') {
            return undefined;
        }
        window.addEventListener('load', jump, {once: true});
        return () => window.removeEventListener('load', jump);
    }, []);

    // The registration page sends a new customer here with ?registered=true once their account exists.
    const justRegistered = typeof window !== 'undefined'
        && new URLSearchParams(window.location.search).get('registered') === 'true';

    const helpHref = content.nav.find((item) => item.label === 'Help')?.href;
    const nav = content.nav.filter((item) => !['Home', 'Try HOTmaths', 'Help'].includes(item.label));
    return (
        <Layout config={config} nav={nav} helpHref={helpHref} productName={content.productName}
                homeHref={content.links?.home} registerHref={content.links?.register}>
            {justRegistered && (
                <div className={styles.registered}>
                    <Alert type="success" showIcon
                           title="Congratulations, you have successfully registered."
                           description="Sign in with your new username and password to get started."/>
                </div>
            )}
            <Hero content={content.intro}/>
            <FeatureGrid content={content.features}/>

            <div className={styles.tryAndPricing}>
                <div className={styles.tryAndPricingInner}>
                    {/* Content's own nav/CTA hrefs point at this exact id (`#ch-home-trial`) and can't
                        change - content/hotmaths.js is shared with Home.js and stays untouched. */}
                    <TrialPanel id="ch-home-trial" content={content.trial} trialDays={config?.trialDays}/>
                    {content.pricing.quote ? (
                        <QuotePanel content={content.pricing}/>
                    ) : (
                        <PricingPanel content={{
                            ...content.pricing,
                            contactEmail: config?.contactUsEmail || content.pricing.contactEmail,
                        }}/>
                    )}
                </div>
            </div>

            {/* Not every brand has a video or public sample lessons. */}
            {content.video && <VideoSection content={content.video}/>}
            {content.sampleLessons && <SampleLessons content={content.sampleLessons}/>}
            {content.benefits && <BenefitsSection content={content.benefits}/>}
            <AboutSection content={content.about}/>
            <OtherProductsCard content={content.otherProducts}/>
        </Layout>
    );
}
